import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Printer } from "lucide-react";
import { printWithTitle } from "@/lib/printWithTitle";
import { getPhotoSpanClass } from "@/lib/photoLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PublicQuickReport — read-only customer-facing view of a Pikaraportti.
 * URL: /q/:shareToken    (?print=1 → auto window.print() for PDF download)
 *
 * Header layout:
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │  [logo]  Yrityksen nimi                  Veraso Reports      │
 *   │          email · phone · website · addr   (small, brand)     │
 *   └──────────────────────────────────────────────────────────────┘
 */
export default function PublicQuickReport() {
  const { shareToken } = useParams();
  const [params] = useSearchParams();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/quick-reports/public/${shareToken}`);
        if (!cancelled) setDoc(data);
      } catch (_e) {
        if (!cancelled) setError("Pikaraporttia ei löytynyt tai linkki on vanhentunut.");
      }
    })();
    return () => { cancelled = true; };
  }, [shareToken]);

  // Auto-print mode (?print=1) — call window.print() once content is ready
  useEffect(() => {
    if (!doc) return;
    if (params.get("print") !== "1") return;
    const label = (doc.title || "").trim();
    const t = setTimeout(
      () => printWithTitle(label ? `Pikaraportti - ${label}` : "Pikaraportti"),
      700
    );
    return () => clearTimeout(t);
  }, [doc, params]);

  // Dynamic browser/PDF document title.
  useEffect(() => {
    if (!doc) return;
    const label = (doc.title || "").trim();
    const prev = document.title;
    document.title = label ? `Pikaraportti - ${label}` : "Pikaraportti";
    return () => { document.title = prev; };
  }, [doc]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="text-center">
          <div className="text-2xl font-display font-semibold text-slate-900 mb-2">Linkki ei toimi</div>
          <div className="text-slate-600">{error}</div>
        </div>
      </div>
    );
  }
  if (!doc) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400">Ladataan…</div>;
  }

  const dateStr = new Date(doc.created_at || Date.now()).toLocaleDateString("fi-FI");
  const company = doc._company || {};
  const contact = doc.contact || {};
  const photos = doc.photos || [];
  const materials = (doc.materials || []).filter((m) => (m.name || "").trim());
  const checklist = (doc.checklist || []).filter((c) => (c.label || "").trim());
  const findings = (doc.findings_text || "").trim();

  const companyContactBits = [
    company.email,
    company.phone,
    company.website,
    company.address,
  ].filter(Boolean);

  // Photo grid logic — see /lib/photoLayout.js. All grids are 2-col with selective spans.
  const renderPhotos = () => {
    if (photos.length === 0) return null;
    const card = "rounded-xl overflow-hidden border border-slate-100 bg-white";
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 print:grid-cols-2 gap-3" data-testid="public-quick-photos-grid">
        {photos.map((p, i) => (
          <figure key={i} className={`${card} ${getPhotoSpanClass(i, photos.length)}`}>
            <img src={p.data} alt={p.caption || ""} className="w-full aspect-[4/3] object-cover bg-slate-50" />
            {p.caption && <figcaption className="px-3 py-2 text-xs text-slate-700 border-t border-slate-100">{p.caption}</figcaption>}
          </figure>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 print:bg-white">
      <style>{`
        @media print {
          /* Setting margin: 0 lets the browser hide its default header/footer
             on Chrome/Edge (they live inside the @page margin band). */
          @page { size: A4; margin: 0; }
          html, body { background: #ffffff !important; margin: 0 !important; padding: 0 !important; }
          .no-print { display: none !important; }
          /* In print, use full A4 content width (210mm minus 14mm side padding). */
          .print-page {
            padding: 14mm !important;
            max-width: none !important;
            width: 100% !important;
          }
        }
      `}</style>

      <div className="max-w-4xl mx-auto p-5 md:p-10 print-page">
        <div className="no-print flex justify-end mb-3">
          <button
            type="button"
            onClick={() => {
              const label = (doc.title || "").trim();
              printWithTitle(label ? `Pikaraportti - ${label}` : "Pikaraportti");
            }}
            className="btn-primary text-sm"
            data-testid="public-quick-print-button"
          >
            <Printer className="h-4 w-4" /> Lataa PDF / Tulosta
          </button>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 md:p-10 print:shadow-none print:border-0 print:p-0">
          {/* Header — modern, balanced */}
          <header className="flex items-start justify-between gap-4 sm:gap-6 pb-5 border-b border-slate-100">
            <div className="flex items-start gap-3 sm:gap-4 min-w-0 flex-1">
              {company.logo ? (
                <img
                  src={company.logo}
                  alt={company.name || "Yritys"}
                  className="h-12 sm:h-14 md:h-16 w-auto max-w-[140px] sm:max-w-[160px] object-contain shrink-0"
                  data-testid="public-quick-company-logo"
                />
              ) : (
                <div className="h-12 w-12 sm:h-14 sm:w-14 md:h-16 md:w-16 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400 text-xs uppercase tracking-wider font-semibold shrink-0">
                  Logo
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="font-display text-base sm:text-lg md:text-xl font-semibold text-slate-900 leading-tight truncate" data-testid="public-quick-company-name">
                  {company.name || "—"}
                </div>
                {companyContactBits.length > 0 && (
                  <div className="mt-1 text-xs md:text-sm text-slate-500 leading-relaxed break-words">
                    {companyContactBits.join(" · ")}
                  </div>
                )}
              </div>
            </div>
            {/* Brand: hidden on mobile (already shown in footer "Powered by Veraso Reports"); inline on >=sm */}
            <div className="hidden sm:block text-right shrink-0">
              <div
                className="font-display text-xs md:text-sm font-semibold tracking-wide text-brand-dark whitespace-nowrap"
                data-testid="public-quick-brand"
              >
                Veraso <span className="text-emerald-600">Reports</span>
              </div>
            </div>
          </header>

          {/* Title */}
          <div className="mt-7">
            <h1 className="font-display text-2xl md:text-4xl font-semibold tracking-tight text-slate-900 leading-tight" data-testid="public-quick-title">
              {doc.title}
            </h1>
            <div className="mt-2 text-sm text-slate-500">
              {dateStr}{doc.customer_name ? ` · ${doc.customer_name}` : ""}
            </div>
          </div>

          {/* Contact details */}
          {(contact.name || contact.phone || contact.email || contact.address || contact.notes) && (
            <Section title="Yhteystiedot" testId="public-quick-contact">
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-slate-800">
                {contact.name && <div><span className="text-slate-500">Yhteyshenkilö:</span> <span className="font-medium">{contact.name}</span></div>}
                {contact.phone && <div><span className="text-slate-500">Puhelin:</span> <a href={`tel:${contact.phone}`} className="font-medium hover:underline">{contact.phone}</a></div>}
                {contact.email && <div><span className="text-slate-500">Sähköposti:</span> <a href={`mailto:${contact.email}`} className="font-medium hover:underline">{contact.email}</a></div>}
                {contact.address && <div><span className="text-slate-500">Osoite:</span> <span className="font-medium">{contact.address}</span></div>}
              </div>
              {contact.notes && (
                <div className="mt-3 text-sm text-slate-700 whitespace-pre-wrap">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Lisätiedot</div>
                  {contact.notes}
                </div>
              )}
            </Section>
          )}

          {/* Body text — Kuvaus / mitä tehtiin */}
          {doc.text_body && (
            <Section title="Kuvaus" testId="public-quick-body">
              <div className="text-slate-800 leading-relaxed whitespace-pre-wrap">{doc.text_body}</div>
            </Section>
          )}

          {/* Findings (only when allowed for customer view) */}
          {findings && (
            <Section title="Havainnot / jatkotoimenpiteet" testId="public-quick-findings" accent="amber">
              <div className="text-slate-800 leading-relaxed whitespace-pre-wrap">{findings}</div>
            </Section>
          )}

          {/* Materials */}
          {materials.length > 0 && (
            <Section title="Käytetyt tarvikkeet" testId="public-quick-materials" accent="blue">
              <ul className="text-sm text-slate-800 grid sm:grid-cols-2 gap-x-6 gap-y-1">
                {materials.map((m, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-slate-400">·</span>
                    <span>
                      <span className="font-medium">{m.name}</span>
                      {(m.qty || m.unit) && (
                        <span className="text-slate-500"> — {[m.qty, m.unit].filter(Boolean).join(" ")}</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {/* Checklist */}
          {checklist.length > 0 && (
            <Section title="Tarkistuslista" testId="public-quick-checklist">
              <ul className="text-sm text-slate-800 space-y-1.5">
                {checklist.map((c) => (
                  <li key={c.id || c.label} className="flex items-start gap-2.5">
                    <span
                      className={`mt-0.5 inline-flex h-4 w-4 items-center justify-center rounded border ${
                        c.checked ? "bg-emerald-500 border-emerald-500 text-white" : "border-slate-300"
                      }`}
                      aria-hidden
                    >
                      {c.checked && <span className="text-[10px] font-bold leading-none">✓</span>}
                    </span>
                    <span className={c.checked ? "text-slate-900" : "text-slate-700"}>{c.label}</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {/* Photos */}
          {photos.length > 0 && (
            <Section title="Kuvat" testId="public-quick-photos">
              {renderPhotos()}
            </Section>
          )}

          {/* Footer */}
          <div className="mt-10 pt-5 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-400">
            <div>Powered by Veraso Reports</div>
            <div>{dateStr}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children, testId, accent }) {
  const accentBorder =
    accent === "amber"
      ? "border-amber-200 bg-amber-50/40"
      : accent === "blue"
      ? "border-blue-200 bg-blue-50/40"
      : "border-slate-100 bg-slate-50/60";
  return (
    <section className={`mt-6 rounded-2xl border ${accentBorder} p-5`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">{title}</div>
      {children}
    </section>
  );
}
